import React from 'react';

import { Input, Button, Tooltip, Dropdown, Menu, Empty, Pagination } from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  MailOutlined,
  DeleteOutlined,
  MoreOutlined,
  UserOutlined,
} from '@ant-design/icons';

// Paleta Alpha
const COR = {
  grafite: '#212121',
  texto: '#1f2937',
  textoSec: '#6b7280',
  textoTerc: '#9ca3af',
  borda: '#e5e7eb',
  bordaSuave: '#f1f5f9',
  fundoClaro: '#f8fafc',
  cabecalho: '#f9fafb',
  branco: '#ffffff',
  vermelho: '#d32f2f',
  vermelhoSuaveBg: '#fef2f2',
  vermelhoSuaveTxt: '#b91c1c',
  verdeSuaveBg: '#f0fdf4',
  verdeSuaveTxt: '#16a34a',
  amareloSuaveBg: '#fffbeb',
  amareloSuaveTxt: '#b45309',
  azulSuaveBg: '#eff6ff',
  azulSuaveTxt: '#0369a1',
};

const BREAKPOINT_CARDS = 1024;

// ---------- StatusBadge ----------
function StatusBadge({ status }) {
  let bg = COR.amareloSuaveBg, txt = COR.amareloSuaveTxt, label = status;
  if (status && status.startsWith('Lista fechada')) {
    bg = COR.verdeSuaveBg; txt = COR.verdeSuaveTxt;
  } else if (status === 'Excedente com diferença a pagar') {
    bg = COR.vermelhoSuaveBg; txt = COR.vermelhoSuaveTxt;
  }
  return (
    <span style={{
      display: 'inline-block',
      background: bg,
      color: txt,
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 700,
      lineHeight: '18px',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// ---------- ActionMenu (mobile / overflow) ----------
function ActionMenu({ record, onEditar, onReenviar, onExcluir, podeReenviar, isAdmin }) {
  const menu = (
    <Menu>
      <Menu.Item key="editar" icon={<EditOutlined />} onClick={() => onEditar(record)}>
        Editar
      </Menu.Item>
      {podeReenviar ? (
        <Menu.Item key="reenviar" icon={<MailOutlined />} onClick={() => onReenviar(record)}>
          Reenviar acesso
        </Menu.Item>
      ) : null}
      {isAdmin ? (
        <Menu.Item key="excluir" danger icon={<DeleteOutlined />} onClick={() => onExcluir(record.id)}>
          Excluir
        </Menu.Item>
      ) : null}
    </Menu>
  );
  return (
    <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
      <Button type="text" icon={<MoreOutlined style={{ fontSize: 18 }} />} />
    </Dropdown>
  );
}

// ---------- Texto truncado com tooltip ----------
function Truncado({ children, title, style }) {
  return (
    <Tooltip title={title || children}>
      <span style={{
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        ...style,
      }}>
        {children}
      </span>
    </Tooltip>
  );
}

class TabelaFormandos extends React.Component {
  state = {
    searchText: '',
    filtroRapido: 'todos',
    paginaAtual: 1,
    pageSize: 8,
    largura: typeof window !== 'undefined' ? window.innerWidth : 1280,
  };

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    // Atualiza só quando cruza o breakpoint, para evitar re-render excessivo
    const novaLargura = window.innerWidth;
    const eraCards = this.state.largura < BREAKPOINT_CARDS;
    const seraCards = novaLargura < BREAKPOINT_CARDS;
    if (eraCards !== seraCards || Math.abs(novaLargura - this.state.largura) > 80) {
      this.setState({ largura: novaLargura });
    }
  };

  // CPF formatado
  formatarCpf(documento) {
    if (!documento) return '-';
    return String(documento).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  // Data de nascimento -> dd/mm/yyyy
  formatarData(data) {
    if (!data || data === '') return '-';
    if (typeof data === 'string') {
      data = data.trim();
      if (data.match(/^\d{4}[-/]\d{2}[-/]\d{2}$/)) {
        const [ano, mes, dia] = data.split(/[-/]/);
        return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`;
      }
      if (data.match(/^\d{2}[-/]\d{2}[-/]\d{4}$/)) return data.replace(/-/g, '/');
      if (data.match(/^\d{2}[-/]\d{2}[-/]\d{2}$/)) {
        const [dia, mes, ano] = data.split(/[-/]/);
        return `${dia}/${mes}/20${ano}`;
      }
    }
    return data;
  }

  // Conjunto de status (por item) que um formando "toca" — usado nos filtros
  // rápidos "Distribuição pendente" e "Lista fechada".
  statusDoFormando(formando) {
    const produtos = this.props.produtosRateio || [];
    const tags = new Set();
    produtos.forEach((r) => {
      const qtd = this.props.valorRateioInput
        ? Number(this.props.valorRateioInput(formando.id, r.item.id)) || 0
        : 0;
      if (qtd <= 0) return;
      if (r.status && r.status.startsWith('Lista fechada')) tags.add('fechada');
      else if (r.status === 'Distribuição pendente') tags.add('pendente');
    });
    return tags;
  }

  formandosFiltrados() {
    const data = this.props.data || [];
    const termo = this.state.searchText.trim().toLowerCase();
    const filtro = this.state.filtroRapido;
    // Versão só-dígitos do termo, para casar CPF/celular independente de pontuação.
    const termoDigitos = termo.replace(/\D/g, '');

    return data.filter((f) => {
      if (termo) {
        // Alvo textual: nome, e-mail, curso, e CPF nas formas formatada e crua.
        const docDigitos = String(f.documento || '').replace(/\D/g, '');
        const cpfFormatado = this.formatarCpf(f.documento);
        const alvo = [f.nome, f.email, f.celular, f.documento, cpfFormatado, f.curso]
          .map((v) => String(v || '').toLowerCase())
          .join(' ');

        // Casa por texto OU, quando o usuário digitou dígitos, por CPF/celular
        // comparando apenas dígitos (ignora pontos, traços e espaços).
        const casaTexto = alvo.includes(termo);
        const casaDigitos = termoDigitos.length > 0 && (
          docDigitos.includes(termoDigitos) ||
          String(f.celular || '').replace(/\D/g, '').includes(termoDigitos)
        );
        if (!casaTexto && !casaDigitos) return false;
      }
      if (filtro !== 'todos') {
        const tags = this.statusDoFormando(f);
        if (!tags.has(filtro)) return false;
      }
      return true;
    });
  }

  setFiltro(filtro) {
    this.setState({ filtroRapido: filtro, paginaAtual: 1 });
  }

  // ---------- Inputs de convite ----------
  renderQtdInput(record, r, compacto) {
    const qtd = this.props.valorRateioInput ? this.props.valorRateioInput(record.id, r.item.id) : 0;
    const bloqueado = this.props.rateioBloqueado === true;
    // Excesso é uma condição da COLUNA (o convite ultrapassou o contratado),
    // não de um formando específico. Por isso destacamos todos os inputs da
    // coluna de forma leve e uniforme — sem apontar quem "estourou", o que era
    // contra-intuitivo (editava um e marcava outro). A divisão da diferença
    // entre formandos é proporcional e tratada no carrinho/back-end.
    const colunaEmExcesso = r.excedente > 0;
    const destacar = colunaEmExcesso && Number(qtd) > 0;

    return (
      <input
        type="number"
        min="0"
        max="99999"
        step="1"
        value={qtd}
        disabled={bloqueado}
        onChange={(e) => this.props.onChangeRateio && this.props.onChangeRateio(record.id, r.item, e.target.value)}
        onBlur={() => this.props.onBlurRateio && this.props.onBlurRateio(record.id, r.item)}
        style={{
          width: compacto ? 60 : 64,
          padding: '5px 6px',
          border: `1px solid ${destacar ? '#fde4c4' : COR.borda}`,
          borderRadius: 8,
          textAlign: 'center',
          fontWeight: 600,
          fontSize: 13,
          background: bloqueado ? '#f1f5f9' : (destacar ? '#fffaf3' : COR.branco),
          color: bloqueado ? COR.textoSec : COR.texto,
          cursor: bloqueado ? 'not-allowed' : 'text',
          outline: 'none',
        }}
      />
    );
  }

  // ---------- Botões de ação (desktop) ----------
  renderAcoesDesktop(record) {
    const podeReenviar = !!record.usuario;
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
        <Tooltip title="Editar formando">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => this.props.editarFormando(record)}
            style={{ color: COR.vermelho, borderColor: '#f3c2c2', background: COR.branco, fontWeight: 500 }}
          >
            Editar
          </Button>
        </Tooltip>
        {podeReenviar ? (
          <Tooltip title="Reenviar acesso ao sistema">
            <Button
              size="small"
              icon={<MailOutlined />}
              onClick={() => this.props.reenviarEmail(record)}
              style={{ color: COR.textoSec, borderColor: COR.borda, background: COR.branco }}
            >
              Reenviar
            </Button>
          </Tooltip>
        ) : null}
        {this.props.isAdmin ? (
          <ActionMenu
            record={record}
            onEditar={this.props.editarFormando}
            onReenviar={this.props.reenviarEmail}
            onExcluir={this.props.excluirFormando}
            podeReenviar={false}
            isAdmin={true}
          />
        ) : null}
      </div>
    );
  }

  // ---------- Linha-card (desktop) ----------
  renderRow(record) {
    const produtos = this.props.produtosRateio || [];
    return (
      <div
        key={record.id}
        style={{
          display: 'grid',
          gridTemplateColumns: this.gridColunas(),
          alignItems: 'center',
          gap: 12,
          padding: '8px 14px',
          background: COR.branco,
          border: `1px solid ${COR.borda}`,
          borderRadius: 10,
          marginBottom: 6,
          transition: 'box-shadow .15s ease, border-color .15s ease',
        }}
        className="formando-row"
      >
        {/* Identificação + contato */}
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            background: COR.vermelhoSuaveBg, color: COR.vermelho,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13,
          }}>
            <UserOutlined />
          </div>
          <div style={{ minWidth: 0 }}>
            <Truncado title={record.nome} style={{ fontWeight: 600, color: COR.texto, fontSize: 13.5, lineHeight: 1.3 }}>
              {record.nome}
            </Truncado>
            <Truncado title={record.email} style={{ color: COR.textoSec, fontSize: 11.5, lineHeight: 1.3 }}>
              {record.email || '-'}
              {record.celular ? <span style={{ color: COR.textoTerc }}>{`  ·  ${record.celular}`}</span> : null}
            </Truncado>
          </div>
        </div>

        {/* Documento + curso */}
        <div style={{ minWidth: 0 }}>
          <div style={{ color: COR.texto, fontSize: 12, lineHeight: 1.35 }}>
            {this.formatarCpf(record.documento)}
            <span style={{ color: COR.textoSec }}>{`  ·  ${this.formatarData(record.dataNascimento)}`}</span>
          </div>
          <Truncado title={record.curso} style={{ color: COR.textoTerc, fontSize: 11.5, lineHeight: 1.3 }}>
            {record.curso || '-'}
          </Truncado>
        </div>

        {/* Convites */}
        {produtos.map((r) => (
          <div key={r.item.id} style={{ display: 'flex', justifyContent: 'center' }}>
            {this.renderQtdInput(record, r, true)}
          </div>
        ))}

        {/* Ações (sticky-friendly, sempre visível) */}
        <div>{this.renderAcoesDesktop(record)}</div>
      </div>
    );
  }

  // ---------- Card (mobile / tablet) ----------
  renderCard(record) {
    const produtos = this.props.produtosRateio || [];
    const podeReenviar = !!record.usuario;
    return (
      <div
        key={record.id}
        style={{
          background: COR.branco,
          border: `1px solid ${COR.borda}`,
          borderRadius: 12,
          padding: 16,
          marginBottom: 12,
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        }}
      >
        {/* Cabeçalho do card */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
            background: COR.vermelhoSuaveBg, color: COR.vermelho,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <UserOutlined />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Truncado title={record.nome} style={{ fontWeight: 700, color: COR.texto, fontSize: 15 }}>
              {record.nome}
            </Truncado>
            <Truncado title={record.email} style={{ color: COR.textoSec, fontSize: 13 }}>
              {record.email || '-'}
            </Truncado>
          </div>
          {this.props.isAdmin ? (
            <ActionMenu
              record={record}
              onEditar={this.props.editarFormando}
              onReenviar={this.props.reenviarEmail}
              onExcluir={this.props.excluirFormando}
              podeReenviar={false}
              isAdmin={true}
            />
          ) : null}
        </div>

        {/* Detalhes secundários */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px 12px',
          padding: '12px 0', borderTop: `1px solid ${COR.bordaSuave}`, borderBottom: `1px solid ${COR.bordaSuave}`,
        }}>
          <Detalhe rotulo="Celular" valor={record.celular || '-'} />
          <Detalhe rotulo="CPF" valor={this.formatarCpf(record.documento)} />
          <Detalhe rotulo="Nascimento" valor={this.formatarData(record.dataNascimento)} />
          <Detalhe rotulo="Curso" valor={record.curso || '-'} />
        </div>

        {/* Distribuição */}
        {produtos.length > 0 ? (
          <div style={{ padding: '12px 0', borderBottom: `1px solid ${COR.bordaSuave}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COR.textoTerc, textTransform: 'uppercase', marginBottom: 8 }}>
              Distribuição de convites
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {produtos.map((r) => (
                <div key={r.item.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 11, color: COR.textoSec, maxWidth: 120, lineHeight: 1.2 }}>
                    {r.item.produto}
                  </span>
                  {this.renderQtdInput(record, r, false)}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Ações no rodapé */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button
            block
            icon={<EditOutlined />}
            onClick={() => this.props.editarFormando(record)}
            style={{ color: COR.vermelho, borderColor: '#f3c2c2', fontWeight: 500 }}
          >
            Editar
          </Button>
          {podeReenviar ? (
            <Button
              block
              icon={<MailOutlined />}
              onClick={() => this.props.reenviarEmail(record)}
              style={{ color: COR.textoSec, borderColor: COR.borda }}
            >
              Reenviar acesso
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  // Grid de colunas do desktop: identificação | doc/curso | N convites | ações
  gridColunas() {
    const n = (this.props.produtosRateio || []).length;
    // Convites recebem peso flexível para ocupar o espaço central e evitar
    // grandes vazios entre "Formando" e "Documento/Curso".
    const convites = Array(n).fill('minmax(78px, 1fr)').join(' ');
    return `minmax(200px, 1.6fr) minmax(120px, 0.9fr) ${convites} minmax(170px, auto)`;
  }

  renderCabecalhoColunas() {
    const produtos = this.props.produtosRateio || [];
    const th = {
      fontSize: 11, fontWeight: 700, color: COR.textoSec,
      textTransform: 'uppercase', letterSpacing: '0.03em',
    };
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: this.gridColunas(),
        gap: 12,
        alignItems: 'end',
        padding: '7px 14px',
        background: COR.cabecalho,
        border: `1px solid ${COR.borda}`,
        borderRadius: 8,
        marginBottom: 6,
      }}>
        <div style={th}>Formando</div>
        <div style={th}>Documento / Curso</div>
        {produtos.map((r) => (
          <div key={r.item.id} style={{
            textAlign: 'center',
            ...(r.excedente > 0 ? {
              background: '#fffaf3',
              border: '1px solid #fde4c4',
              borderRadius: 8,
              padding: '4px 6px',
              margin: '-4px -2px',
            } : {}),
          }}>
            <div style={{ ...th, fontSize: 10.5 }}>{r.item.produto}</div>
            <div style={{ fontSize: 10, color: r.excedente > 0 ? COR.amareloSuaveTxt : COR.textoTerc, marginTop: 2 }}>
              {r.distribuida}/{r.contratada}
              {r.excedente > 0 ? (
                <span style={{ color: COR.amareloSuaveTxt, fontWeight: 700 }}> · +{r.excedente} excesso</span>
              ) : null}
            </div>
          </div>
        ))}
        <div style={{ ...th, textAlign: 'right' }}>Ações</div>
      </div>
    );
  }

  // Linha de status por convite (somente desktop, abaixo das linhas)
  renderRodapeStatus() {
    const produtos = this.props.produtosRateio || [];
    if (produtos.length === 0) return null;
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: this.gridColunas(),
        gap: 12,
        alignItems: 'center',
        padding: '12px 16px',
        background: COR.fundoClaro,
        border: `1px solid ${COR.borda}`,
        borderRadius: 10,
        marginTop: 6,
      }}>
        <div style={{ fontWeight: 700, color: COR.texto, fontSize: 12 }}>Status por convite</div>
        <div />
        {produtos.map((r) => (
          <div key={r.item.id} style={{ textAlign: 'center' }}>
            <StatusBadge status={r.status} />
          </div>
        ))}
        <div />
      </div>
    );
  }

  renderFiltros() {
    const produtos = this.props.produtosRateio || [];
    const temRateio = produtos.length > 0;
    const data = this.props.data || [];

    // Conta quantos formandos casam com cada filtro (para exibir no chip e
    // deixar claro quando um filtro está vazio em vez de parecer "quebrado").
    const contar = (key) => {
      if (key === 'todos') return data.length;
      return data.filter((f) => this.statusDoFormando(f).has(key)).length;
    };

    // O número entre parênteses é a contagem de FORMANDOS que casam com o
    // filtro — não a quantidade de unidades. Ex.: "Com excesso (3)" = 3
    // formandos têm convites num item em excesso (o total de unidades em
    // excesso aparece no resumo inferior, em "Excedente").
    const filtros = [
      { key: 'todos', label: 'Todos', dica: 'Todos os formandos cadastrados.' },
      ...(temRateio ? [
        { key: 'pendente', label: 'Distribuição pendente', dica: 'Formandos com convites em itens ainda não totalmente distribuídos.' },
        { key: 'fechada', label: 'Lista fechada', dica: 'Formandos com convites em itens já fechados.' },
      ] : []),
    ];
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {filtros.map((f) => {
          const ativo = this.state.filtroRapido === f.key;
          const total = contar(f.key);
          return (
            <Tooltip key={f.key} title={f.dica}>
              <button
                onClick={() => this.setFiltro(f.key)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 20,
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: `1px solid ${ativo ? COR.vermelho : COR.borda}`,
                  background: ativo ? COR.vermelho : COR.branco,
                  color: ativo ? COR.branco : COR.textoSec,
                  transition: 'all .15s ease',
                }}
              >
                {f.label}
                <span style={{
                  marginLeft: 6,
                  opacity: 0.8,
                  fontWeight: 700,
                }}>({total})</span>
              </button>
            </Tooltip>
          );
        })}
      </div>
    );
  }

  render() {
    const isCards = this.state.largura < BREAKPOINT_CARDS;
    const todos = this.formandosFiltrados();
    const { paginaAtual, pageSize } = this.state;
    const inicio = (paginaAtual - 1) * pageSize;
    const pagina = todos.slice(inicio, inicio + pageSize);

    return (
      <div>
        <style>{`
          .formando-row:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.06); border-color: #d1d5db !important; }
          .formando-busca .ant-input-affix-wrapper { border-radius: 10px; }
        `}</style>

        {/* Busca global */}
        <div className="formando-busca" style={{ marginBottom: 12, maxWidth: 420 }}>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: COR.textoTerc }} />}
            placeholder="Buscar por nome, e-mail, CPF ou curso"
            value={this.state.searchText}
            onChange={(e) => this.setState({ searchText: e.target.value, paginaAtual: 1 })}
            size="large"
          />
        </div>

        {/* Filtros rápidos */}
        {this.renderFiltros()}

        {/* Conteúdo */}
        {pagina.length === 0 ? (
          <div style={{ padding: '40px 0', background: COR.branco, border: `1px solid ${COR.borda}`, borderRadius: 12 }}>
            <Empty description={
              this.state.filtroRapido !== 'todos'
                ? 'Nenhum formando neste filtro. Nenhum convite está com esse status no momento.'
                : 'Nenhum formando encontrado'
            } />
          </div>
        ) : isCards ? (
          <div>{pagina.map((f) => this.renderCard(f))}</div>
        ) : (
          <div>
            {this.renderCabecalhoColunas()}
            {pagina.map((f) => this.renderRow(f))}
            {this.renderRodapeStatus()}
          </div>
        )}

        {/* Paginação */}
        {todos.length > pageSize ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Pagination
              current={paginaAtual}
              pageSize={pageSize}
              total={todos.length}
              showSizeChanger={false}
              onChange={(p) => this.setState({ paginaAtual: p })}
            />
          </div>
        ) : null}
      </div>
    );
  }
}

// Detalhe rotulado (usado nos cards)
function Detalhe({ rotulo, valor }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: COR.textoTerc, textTransform: 'uppercase' }}>{rotulo}</div>
      <Tooltip title={valor}>
        <div style={{
          fontSize: 13, color: COR.texto,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{valor}</div>
      </Tooltip>
    </div>
  );
}

export default TabelaFormandos;
